import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineMailOutline } from "react-icons/md";
import { TbQrcode } from "react-icons/tb";
import { SettingRow, TextInput, Section } from "./SettingsControls";
import { EmailTexts, EmailSubjects, DunningFines } from "./useSettings";
import { EMAIL_DEFAULTS } from "src/config/emailDefaults";
import { useAuth } from "src/context/AuthProvider";
import Popup from "src/components/Popup/Popup";
import { apiRequest } from "src/services/apiClient";

export type SmtpFields = {
    smtpHost:     string;
    smtpPort:     number;
    smtpSecure:   boolean;
    smtpUser:     string;
    smtpKey:      string;
    smtpFrom:     string;
    smtpFromName: string;
    smtpEnabled:  boolean;
};

const EMPTY_SMTP: SmtpFields = {
    smtpHost: "", smtpPort: 587, smtpSecure: false,
    smtpUser: "", smtpKey: "", smtpFrom: "", smtpFromName: "", smtpEnabled: true,
};

interface SmtpSettingsProps {
    smtp:                  SmtpFields;
    emailTexts:            EmailTexts;
    emailSubjects:         EmailSubjects;
    saved:                 boolean;
    dirty?:                boolean;
    view?:                 "smtp" | "templates";
    dunningFinesEnabled?:  boolean;
    dunningFines?:         DunningFines;
    /** Cumulative days-from-sent per dunning level ([0, M1day, M2day, M3day, ...]) — used so
     *  the template preview's sample dates reflect the praxis's actual configured intervals
     *  instead of a generic fixed offset. */
    dunningDays?:          number[];
    onChange:              (s: SmtpFields) => void;
    onEmailTextsChange:    (t: EmailTexts) => void;
    onEmailSubjectsChange: (s: EmailSubjects) => void;
    onSave:                () => void;
}

const isConfigured = (s: SmtpFields) =>
    s.smtpEnabled && s.smtpHost.trim() && s.smtpUser.trim() && s.smtpKey.trim() && s.smtpFrom.trim();

const BASE_VARS = [
    "{praxis.name}", "{praxis.address}", "{praxis.plz}", "{praxis.city}",
    "{praxis.specialization}", "{praxis.phone}", "{praxis.email}",
    "{doctor.salutation}", "{doctor.title}", "{doctor.name}",
    "{recipient.name}",
];
const INVOICE_VARS   = [...BASE_VARS, "{invoice.id}", "{invoice.date}", "{invoice.dueDate}", "{link}"];
const DUNNING_VARS   = [...BASE_VARS, "{invoice.id}", "{invoice.date}", "{invoice.dueDate}", "{invoice.amount}", "{praxis.iban}", "{link}", "{fine}", "{invoice.totalWithFine}", "{M1-date}", "{M2-date}", "{invoice.table}", "{invoice.qr}"];
const QUESTIONNAIRE_VARS = [...BASE_VARS, "{link}"];
const DUNNING_FINE_LEVELS = ["friendly", "demanding", "final"] as const;
const SAMPLE_INVOICE_AMOUNT = 90;

const fmtEUR = (n: number) =>
    n.toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

function VarChips({ vars, onInsert }: { vars: string[]; onInsert: (v: string) => void }) {
    return (
        <div className="pdf-var-chips">
            {vars.map((v) => (
                <button key={v} type="button" className="pdf-var-chip" onClick={() => onInsert(v)}>
                    {v}
                </button>
            ))}
        </div>
    );
}

export function SmtpSettings({ smtp, emailTexts, emailSubjects, saved, dirty, view = "smtp", dunningFinesEnabled, dunningFines, dunningDays, onChange, onEmailTextsChange, onEmailSubjectsChange, onSave }: SmtpSettingsProps) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [templateTab, setTemplateTab] = useState<keyof EmailTexts>("invoice");
    const [editingBody, setEditingBody] = useState(false);
    const [editingSubject, setEditingSubject] = useState(false);
    const [sendPreviewKey, setSendPreviewKey] = useState<keyof EmailTexts | null>(null);
    const [sendPreviewState, setSendPreviewState] = useState<"idle" | "sending" | "ok" | "error">("idle");
    const [confirmSendKey, setConfirmSendKey] = useState<keyof EmailTexts | null>(null);
    const [showTestPopup, setShowTestPopup] = useState(false);
    const [testEmail, setTestEmail] = useState(user?.doctor?.email ?? "");
    const [testState, setTestState] = useState<"idle" | "sending" | "ok" | "error">("idle");
    const [testError, setTestError] = useState("");
    const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);

    // Reset edit mode when switching template tabs
    useEffect(() => {
        setEditingBody(false);
        setEditingSubject(false);
    }, [templateTab]);

    const currentFine = dunningFinesEnabled && (DUNNING_FINE_LEVELS as readonly string[]).includes(templateTab)
        ? (dunningFines?.[templateTab as typeof DUNNING_FINE_LEVELS[number]] ?? 0)
        : 0;

    // Sample dates for an invoice sent today ({invoice.date}), counted forward from the praxis's
    // configured dunning days (cumulative days after sending). Mirrors the backend exactly:
    // dueDate = sentDate + cumDays[1] (same for every level — it's the invoice's due date), and
    // dunningCron sends level L on sentDate + cumDays[L] + 1 (the +1 buffer day in dunningCron).
    const cumDays = dunningDays?.length ? dunningDays : [0, 7, 14, 21, 28];
    const addDays = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toLocaleDateString("de-DE"); };
    const daysTo = (level: number) => cumDays[level] ?? cumDays[1] ?? 7;
    const dueDateStr = addDays(daysTo(1));
    const m1DateStr  = addDays(daysTo(1) + 1);
    const m2DateStr  = addDays(daysTo(2) + 1);

    const SAMPLE_VARS: Record<string, string> = {
        "{praxis.name}":           user?.praxis?.name           ?? "Muster Praxis",
        "{praxis.address}":        user?.praxis?.street         ?? "Musterstraße 1",
        "{praxis.plz}":            user?.praxis?.plz            ?? "12345",
        "{praxis.city}":           user?.praxis?.city           ?? "Berlin",
        "{praxis.specialization}": (user?.praxis as any)?.specialization ?? "Allgemeinmedizin",
        "{praxis.phone}":          ((user?.praxis as any)?.tel ?? [])[0] ?? "+49 30 123456",
        "{praxis.email}":          user?.praxis?.email          ?? "praxis@example.com",
        "{doctor.salutation}":     user?.doctor?.salutation     ?? "Herr",
        "{doctor.title}":          user?.doctor?.title          ?? "Dr.",
        "{doctor.name}":           [user?.doctor?.title, user?.doctor?.firstName, user?.doctor?.lastName].filter(Boolean).join(" ") || "Dr. Max Mustermann",
        "{recipient.name}":        "Erika Mustermann",
        "{invoice.id}":            "RE-2024-0001",
        "{invoice.date}":          new Date().toLocaleDateString("de-DE"),
        "{invoice.dueDate}":       dueDateStr,
        "[date]":                  dueDateStr,
        "{link}":                  "https://portal.example.com/invoices/preview-link",
        "{fine}":                  fmtEUR(currentFine),
        "{invoice.totalWithFine}": fmtEUR(SAMPLE_INVOICE_AMOUNT + currentFine),
        // Doctor's own payout IBAN takes priority — praxis.iban can be overwritten by the
        // platform billing SEPA mandate and must never be shown as the patient payout account
        // unless the doctor hasn't set their own (matches backend resolveEmailVars.ts).
        "{praxis.iban}":           user?.doctor?.iban || user?.praxis?.iban || "DE12 3456 7890 1234 5678 90",
        "{M1-date}":               m1DateStr,
        "{M2-date}":               m2DateStr,
    };

    // plainOnly=true drops the {if:qr} block entirely — used for the plain-text email body,
    // which can't show an image. Otherwise (the rich view-zone preview, and the actual
    // test-send HTML body) the block is kept and {invoice.qr} is left as a literal token: the
    // preview's paragraph loop renders it specially, and the backend's /email/send-preview
    // resolves it into a real inline QR image (cid attachment), same as a live dunning email.
    const renderTemplate = (text: string, plainOnly = false) => {
        const withConditionals = text
            .replace(/\{if:fine\}([\s\S]*?)\{\/if\}/g, currentFine > 0 ? "$1" : "")
            .replace(/\{if:qr\}([\s\S]*?)\{\/if\}/g, plainOnly ? "" : "$1");
        const resolved = Object.entries(SAMPLE_VARS).reduce((s, [k, v]) => s.replaceAll(k, v), withConditionals);
        return plainOnly ? resolved.replace(/\{invoice\.qr\}/g, "") : resolved;
    };

    const handleSendPreview = async (key: keyof EmailTexts) => {
        const row = EMAIL_TEMPLATE_ROWS.find((r) => r.key === key)!;
        const subject = renderTemplate(emailSubjects[key] || row.defaultSubject, true);
        const text    = renderTemplate(emailTexts[key]    || row.defaultText, false);
        const to      = user?.doctor?.email;
        if (!to) return;
        setConfirmSendKey(null);
        setSendPreviewKey(key);
        setSendPreviewState("sending");
        try {
            const res = await apiRequest<{ ok: boolean }>(
                "/settings/email/send-preview",
                {
                    method: "POST",
                    // amount/fine/dueDate let the backend build a sample table that matches
                    // this level's actual configured fine — it previously used a hardcoded
                    // "5,00 €" / "95,00 €" regardless of the real amount shown in the prose text.
                    body: JSON.stringify({
                        to, subject, text,
                        invoiceId: SAMPLE_VARS["{invoice.id}"],
                        dueDate:   SAMPLE_VARS["{invoice.dueDate}"],
                        amount:    SAMPLE_INVOICE_AMOUNT,
                        fine:      currentFine,
                    }),
                },
                { "Content-Type": "application/json" },
            );
            setSendPreviewState(res.ok ? "ok" : "error");
        } catch {
            setSendPreviewState("error");
        }
        setTimeout(() => { setSendPreviewKey(null); setSendPreviewState("idle"); }, 3000);
    };

    const EMAIL_TEMPLATE_ROWS: { key: keyof EmailTexts; label: string; description: string; defaultSubject: string; defaultText: string; vars: string[] }[] = [
        { key: "invoice",       label: t("settings.emailTemplateInvoice"),       description: t("settings.emailTemplateInvoiceDesc"),       defaultSubject: EMAIL_DEFAULTS.portalLink.subject,    defaultText: EMAIL_DEFAULTS.invoice.text,       vars: INVOICE_VARS },
        { key: "resent",        label: t("settings.emailTemplateResent"),        description: t("settings.emailTemplateResentDesc"),        defaultSubject: EMAIL_DEFAULTS.resent.subject,        defaultText: EMAIL_DEFAULTS.resent.text,         vars: INVOICE_VARS },
        { key: "friendly",      label: `M1 – ${t("settings.emailTemplateFriendly")}`,      description: t("settings.emailTemplateFriendlyDesc"),      defaultSubject: EMAIL_DEFAULTS.friendly.subject,      defaultText: EMAIL_DEFAULTS.friendly.text,       vars: DUNNING_VARS },
        { key: "demanding",     label: `M2 – ${t("settings.emailTemplateDemanding")}`,     description: t("settings.emailTemplateDemandingDesc"),     defaultSubject: EMAIL_DEFAULTS.demanding.subject,     defaultText: EMAIL_DEFAULTS.demanding.text,      vars: DUNNING_VARS },
        { key: "final",         label: `M3 – ${t("settings.emailTemplateFinal")}`,         description: t("settings.emailTemplateFinalDesc"),         defaultSubject: EMAIL_DEFAULTS.final.subject,         defaultText: EMAIL_DEFAULTS.final.text,          vars: DUNNING_VARS },
        { key: "questionnaire", label: t("settings.emailTemplateQuestionnaire"), description: t("settings.emailTemplateQuestionnaireDesc"), defaultSubject: EMAIL_DEFAULTS.questionnaire.subject, defaultText: EMAIL_DEFAULTS.questionnaire.text,  vars: QUESTIONNAIRE_VARS },
    ];

    const configured = isConfigured(smtp);

    const handleTest = async () => {
        if (!testEmail.trim()) return;
        setTestState("sending");
        setTestError("");
        try {
            const res = await apiRequest<{ ok: boolean; error?: string }>(
                "/settings/smtp/test",
                { method: "POST", body: JSON.stringify({ ...smtp, to: testEmail.trim() }) },
                { "Content-Type": "application/json" },
            );
            if (res.ok) {
                setTestState("ok");
            } else {
                setTestState("error");
                setTestError(res.error ?? t("settings.smtpTestFailed"));
            }
        } catch (e: any) {
            setTestState("error");
            setTestError(e?.message ?? t("settings.smtpTestFailed"));
        }
    };

    const closeTestPopup = () => {
        setShowTestPopup(false);
        setTestState("idle");
        setTestEmail("");
        setTestError("");
    };

    return (
        <>
            <Section
                title={view === "smtp" ? t("settings.tabSmtp") : t("settings.emailTemplates")}
                actions={
                    view === "smtp" ? (
                        <button
                            type="button"
                            className="btn-outline btn-outline--danger"
                            onClick={() => onChange({ ...smtp, ...EMPTY_SMTP, smtpEnabled: smtp.smtpEnabled })}
                        >
                            {t("settings.smtpClear")}
                        </button>
                    ) : undefined
                }
            >
                {view === "smtp" && (
                    <>
                    <SettingRow title={t("settings.smtpActive")} description={t("settings.smtpActiveDesc")}>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={smtp.smtpEnabled}
                                onChange={(e) => onChange({ ...smtp, smtpEnabled: e.target.checked })}
                            />
                            <span className="toggle-switch__track" />
                        </label>
                    </SettingRow>
                    <div className="setting-row">
                        <div className={`settings-info-banner settings-info-banner--${configured ? "ok" : "neutral"}`}>
                            <MdOutlineMailOutline size={16} />
                            {!smtp.smtpEnabled ? t("settings.smtpDisabled") : configured ? t("settings.smtpConfigured") : t("settings.smtpNotConfigured")}
                        </div>
                    </div>
                    <SettingRow title={t("settings.smtpHost")} description={t("settings.smtpHostDesc")} disabled={!smtp.smtpEnabled}>
                        <TextInput value={smtp.smtpHost} onChange={(v) => onChange({ ...smtp, smtpHost: v })} placeholder="smtp.example.com" />
                    </SettingRow>
                    <SettingRow title={t("settings.smtpPort")} description={t("settings.smtpPortDesc")} disabled={!smtp.smtpEnabled}>
                        <TextInput
                            value={String(smtp.smtpPort ?? 587)}
                            onChange={(v) => onChange({ ...smtp, smtpPort: parseInt(v, 10) || 587 })}
                            placeholder="587"
                        />
                    </SettingRow>
                    <SettingRow title={t("settings.smtpSecure")} description={t("settings.smtpSecureDesc")} disabled={!smtp.smtpEnabled}>
                        <label className="toggle-label">
                            <input
                                type="checkbox"
                                checked={!!smtp.smtpSecure}
                                onChange={(e) => onChange({ ...smtp, smtpSecure: e.target.checked })}
                            />
                            {t("settings.smtpSecureLabel")}
                        </label>
                    </SettingRow>
                    <SettingRow title={t("settings.smtpUsername")} description={t("settings.smtpUsernameDesc")} disabled={!smtp.smtpEnabled}>
                        <TextInput value={smtp.smtpUser} onChange={(v) => onChange({ ...smtp, smtpUser: v })} placeholder="user@example.com" />
                    </SettingRow>
                    <SettingRow title={t("settings.smtpPassword")} description={t("settings.smtpPasswordDesc")} disabled={!smtp.smtpEnabled}>
                        <TextInput
                            type="password"
                            sanitize={false}
                            value={smtp.smtpKey}
                            onChange={(v) => onChange({ ...smtp, smtpKey: v })}
                            placeholder="••••••••"
                        />
                    </SettingRow>
                    <SettingRow title={t("settings.smtpFromAddress")} description={t("settings.smtpFromAddressDesc")} disabled={!smtp.smtpEnabled}>
                        <TextInput value={smtp.smtpFrom} onChange={(v) => onChange({ ...smtp, smtpFrom: v })} placeholder="praxis@example.com" />
                    </SettingRow>
                    <SettingRow title={t("settings.smtpFromName")} description={t("settings.smtpFromNameDesc")} disabled={!smtp.smtpEnabled}>
                        <TextInput value={smtp.smtpFromName} onChange={(v) => onChange({ ...smtp, smtpFromName: v })} placeholder={t("settings.smtpFromNamePlaceholder")} />
                    </SettingRow>
                    <SettingRow title={t("settings.smtpTest")} description={t("settings.smtpTestDesc")} disabled={!smtp.smtpEnabled}>
                        <button
                            type="button"
                            className="popup__btn popup__btn--default smtp-test-btn"
                            onClick={() => setShowTestPopup(true)}
                            disabled={!configured}
                        >
                            {t("settings.smtpTest")}
                        </button>
                    </SettingRow>
                    </>
                )}

                {view === "templates" && (() => {
                    const currentRow = EMAIL_TEMPLATE_ROWS.find((r) => r.key === templateTab)!;
                    const praxisName = user?.praxis?.name ?? "Muster Praxis";
                    const footer = [
                        praxisName,
                        user?.praxis?.street ?? "Musterstraße 1",
                        (user?.praxis?.plz ?? "12345") + " " + (user?.praxis?.city ?? "Berlin"),
                    ].join(" · ");

                    const showWarning = templateTab === "final";

                    const rawText        = emailTexts[templateTab] || currentRow.defaultText;
                    const renderedText   = renderTemplate(rawText);
                    const renderedSubject = renderTemplate(emailSubjects[templateTab] || currentRow.defaultSubject);

                    // Extract URL-only lines out of their parent paragraphs so they render as buttons
                    const paragraphs: string[] = [];
                    for (const chunk of renderedText.split("\n\n")) {
                        if (!chunk.trim()) continue;
                        const lines = chunk.split("\n");
                        const cur: string[] = [];
                        for (const line of lines) {
                            if (/^https?:\/\/\S+$/.test(line.trim())) {
                                if (cur.length > 0) { paragraphs.push(cur.join("\n")); cur.length = 0; }
                                paragraphs.push(line.trim());
                            } else {
                                cur.push(line);
                            }
                        }
                        if (cur.length > 0) paragraphs.push(cur.join("\n"));
                    }

                    const startEditingBody = () => {
                        if (!emailTexts[templateTab]) {
                            onEmailTextsChange({ ...emailTexts, [templateTab]: currentRow.defaultText });
                        }
                        setEditingBody(true);
                        setTimeout(() => bodyTextareaRef.current?.focus(), 40);
                    };

                    const startEditingSubject = () => {
                        if (!emailSubjects[templateTab]) {
                            onEmailSubjectsChange({ ...emailSubjects, [templateTab]: currentRow.defaultSubject });
                        }
                        setEditingSubject(true);
                    };

                    return (
                        <div className="email-tpl">
                            {/* Template type tabs */}
                            <div className="settings-tabs settings-tabs--secondary">
                                {EMAIL_TEMPLATE_ROWS.map(({ key, label }) => (
                                    <button
                                        key={key}
                                        className={`settings-tab settings-tab--sm${templateTab === key ? " settings-tab--active" : ""}`}
                                        onClick={() => setTemplateTab(key)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            <div className="email-tpl__content">
                                {/* Subject row — above the email card */}
                                <div className="email-tpl__subject-row">
                                    <span className="email-tpl__field-label">{t("settings.emailSubject")}</span>
                                    {editingSubject ? (
                                        <div className="email-tpl__subject-edit">
                                            <input
                                                className="email-tpl__subject-input"
                                                value={emailSubjects[templateTab]}
                                                onChange={(e) => onEmailSubjectsChange({ ...emailSubjects, [templateTab]: e.target.value })}
                                                placeholder={currentRow.defaultSubject}
                                                autoFocus
                                                onBlur={() => setEditingSubject(false)}
                                                onKeyDown={(e) => e.key === "Escape" && setEditingSubject(false)}
                                            />
                                            <VarChips
                                                vars={currentRow.vars}
                                                onInsert={(v) => onEmailSubjectsChange({ ...emailSubjects, [templateTab]: emailSubjects[templateTab] + v })}
                                            />
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            className="email-tpl__subject-display"
                                            onClick={startEditingSubject}
                                        >
                                            <span className="email-tpl__subject-text">
                                                {renderedSubject || <span className="email-tpl__subject-placeholder">{renderTemplate(currentRow.defaultSubject)}</span>}
                                            </span>
                                            <span className="email-tpl__edit-tag">bearbeiten</span>
                                        </button>
                                    )}
                                </div>

                                {/* Email card */}
                                <div className="email-tpl__card">
                                    {/* Praxis header bar */}
                                    <div className="email-tpl__header">{praxisName}</div>

                                    <div className="email-tpl__body">
                                        {/* M3 warning banner */}
                                        {showWarning && (
                                            <div className="email-tpl__warning">
                                                <strong>⚠ Letzte Mahnung</strong>
                                                <p>Sollte die Rechnung nicht sofort beglichen werden, wird die Forderung an ein Inkassobüro übergeben bzw. das außergerichtliche Mahnverfahren eingeleitet.</p>
                                            </div>
                                        )}

                                        {/* Body — click to edit */}
                                        {editingBody ? (
                                            <div className="email-tpl__edit-zone">
                                                <textarea
                                                    ref={bodyTextareaRef}
                                                    className="email-tpl__textarea"
                                                    value={emailTexts[templateTab]}
                                                    onChange={(e) => onEmailTextsChange({ ...emailTexts, [templateTab]: e.target.value })}
                                                    placeholder={currentRow.defaultText}
                                                    rows={10}
                                                />
                                                <VarChips
                                                    vars={currentRow.vars}
                                                    onInsert={(v) => {
                                                        const el = bodyTextareaRef.current;
                                                        if (el) {
                                                            const start = el.selectionStart;
                                                            const end   = el.selectionEnd;
                                                            const cur   = emailTexts[templateTab];
                                                            const next  = cur.slice(0, start) + v + cur.slice(end);
                                                            onEmailTextsChange({ ...emailTexts, [templateTab]: next });
                                                            setTimeout(() => {
                                                                el.focus();
                                                                el.setSelectionRange(start + v.length, start + v.length);
                                                            }, 0);
                                                        } else {
                                                            onEmailTextsChange({ ...emailTexts, [templateTab]: emailTexts[templateTab] + v });
                                                        }
                                                    }}
                                                />
                                                {(templateTab === "friendly" || templateTab === "demanding" || templateTab === "final") && (
                                                    <div className="email-template-hint">
                                                        <strong>{"{if:fine}"}…{"{/if}"}</strong> — dieser Block erscheint nur, wenn eine Mahngebühr konfiguriert ist.
                                                    </div>
                                                )}
                                                {emailTexts[templateTab] && (
                                                    <button
                                                        type="button"
                                                        className="email-tpl__reset-btn"
                                                        onClick={() => onEmailTextsChange({ ...emailTexts, [templateTab]: "" })}
                                                    >
                                                        Standard wiederherstellen
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="email-tpl__done-btn"
                                                    onClick={() => setEditingBody(false)}
                                                >
                                                    Fertig
                                                </button>
                                            </div>
                                        ) : (
                                            <div
                                                className="email-tpl__view-zone"
                                                onClick={startEditingBody}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => e.key === "Enter" && startEditingBody()}
                                            >
                                                {paragraphs.map((para, i) => {
                                                    const trimmed = para.trim();
                                                    if (trimmed === "{invoice.table}") {
                                                        return (
                                                            <div key={i} className="email-tpl__table-block">
                                                                <span className="email-tpl__table-badge">Rechnungsdetails — automatisch</span>
                                                                <table className="email-tpl__table">
                                                                    <tbody>
                                                                        <tr><td>Rechnungsnummer</td><td>{SAMPLE_VARS["{invoice.id}"]}</td></tr>
                                                                        <tr><td>Fälligkeitsdatum</td><td className="email-tpl__table-due">{SAMPLE_VARS["{invoice.dueDate}"]}</td></tr>
                                                                        <tr><td>Betrag</td><td><strong>{fmtEUR(SAMPLE_INVOICE_AMOUNT)}</strong></td></tr>
                                                                        {currentFine > 0 && <tr><td>Mahngebühr</td><td>{SAMPLE_VARS["{fine}"]}</td></tr>}
                                                                        <tr><td>IBAN</td><td className="email-tpl__table-iban">{SAMPLE_VARS["{praxis.iban}"]}</td></tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        );
                                                    }
                                                    if (trimmed === "{invoice.qr}") {
                                                        return (
                                                            <div key={i} className="email-tpl__qr-block">
                                                                <span className="email-tpl__qr-icon"><TbQrcode /></span>
                                                                <span className="email-tpl__qr-label">QR-Code zum Bezahlen — automatisch</span>
                                                            </div>
                                                        );
                                                    }
                                                    if (/^https?:\/\//.test(trimmed)) {
                                                        const btnLabel = templateTab === "questionnaire"
                                                            ? "Einwilligung erteilen →"
                                                            : "Rechnung ansehen →";
                                                        return (
                                                            <div key={i} className="email-tpl__btn-row">
                                                                <span className="email-tpl__btn-preview">{btnLabel}</span>
                                                            </div>
                                                        );
                                                    }
                                                    return <p key={i} className="email-tpl__para">{para}</p>;
                                                })}
                                                <span className="email-tpl__edit-hint">Klicken zum Bearbeiten</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer stripe */}
                                    <div className="email-tpl__footer">{footer}</div>
                                </div>

                                {/* Send to me */}
                                <div className="email-tpl__actions">
                                    <button
                                        type="button"
                                        className={`email-preview-btn email-preview-btn--send${sendPreviewKey === templateTab && sendPreviewState === "ok" ? " email-preview-btn--ok" : sendPreviewKey === templateTab && sendPreviewState === "error" ? " email-preview-btn--error" : ""}`}
                                        onClick={() => setConfirmSendKey(templateTab)}
                                        disabled={sendPreviewKey === templateTab && sendPreviewState === "sending"}
                                    >
                                        {sendPreviewKey === templateTab && sendPreviewState === "sending"
                                            ? t("settings.smtpTestSending")
                                            : sendPreviewKey === templateTab && sendPreviewState === "ok"
                                            ? t("settings.smtpTestOk")
                                            : sendPreviewKey === templateTab && sendPreviewState === "error"
                                            ? t("settings.smtpTestFailed")
                                            : t("settings.emailSendToMe")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Section>

            {showTestPopup && (
                <Popup
                    title={t("settings.smtpTestTitle")}
                    message={
                        <div className="smtp-test-popup">
                            {testState === "idle" || testState === "sending" ? (
                                <>
                                    <p className="smtp-test-popup__desc">{t("settings.smtpTestDesc")}</p>
                                    <input
                                        className="smtp-test-popup__input"
                                        type="email"
                                        value={testEmail}
                                        onChange={(e) => setTestEmail(e.target.value)}
                                        placeholder="test@example.com"
                                        disabled={testState === "sending"}
                                        onKeyDown={(e) => e.key === "Enter" && handleTest()}
                                    />
                                </>
                            ) : testState === "ok" ? (
                                <p className="smtp-test-popup__ok">{t("settings.smtpTestOk")}</p>
                            ) : (
                                <div className="smtp-test-popup__error">
                                    <p>{t("settings.smtpTestFailed")}</p>
                                    {testError && <code className="smtp-test-popup__code">{testError}</code>}
                                </div>
                            )}
                        </div>
                    }
                    actions={
                        testState === "ok" || testState === "error"
                            ? [{ label: t("popup.ok"), variant: "primary" as const, onClick: closeTestPopup }]
                            : [
                                { label: t("popup.cancel"), variant: "default" as const, onClick: closeTestPopup },
                                { label: testState === "sending" ? t("settings.smtpTestSending") : t("settings.smtpTestSend"), variant: "primary" as const, onClick: handleTest, disabled: testState === "sending" || !testEmail.trim() },
                            ]
                    }
                    onClose={closeTestPopup}
                />
            )}

            {confirmSendKey && (() => {
                const from = smtp.smtpFrom?.trim() || "Medinvoice (System)";
                const to   = user?.doctor?.email ?? "";
                return (
                    <Popup
                        title={t("settings.emailSendToMe")}
                        message={
                            <div className="smtp-test-popup">
                                <p className="smtp-test-popup__desc">{t("settings.emailSendConfirmDesc")}</p>
                                <div className="email-send-confirm__row">
                                    <span className="email-send-confirm__label">{t("settings.smtpFromAddress")}:</span>
                                    <span>{from}</span>
                                </div>
                                <div className="email-send-confirm__row">
                                    <span className="email-send-confirm__label">{t("settings.emailSendTo")}:</span>
                                    <span>{to}</span>
                                </div>
                            </div>
                        }
                        actions={[
                            { label: t("popup.cancel"), variant: "default" as const, onClick: () => setConfirmSendKey(null) },
                            { label: t("settings.emailSendToMe"), variant: "primary" as const, onClick: () => handleSendPreview(confirmSendKey) },
                        ]}
                        onClose={() => setConfirmSendKey(null)}
                    />
                );
            })()}
        </>
    );
}
